import { useUserStore } from '@/features/user/stores/user.store'
import { useDatapoints } from '@/composables/useDatapoints'
import type { TourResolveContext } from '../types/tour.types'

/**
 * Builds the availability context (admin + feature flags) used to gate tours,
 * mirroring how AppSidebar gates nav items. A tour's `featureFlag` is the name of
 * an exposed `useDatapoints()` boolean (e.g. `enableLeadEnrichment`, `enableLookAlike`).
 * Fails open on flags (unknown/error → available) but never treats a non-admin as admin.
 */
export function buildTourContext(): TourResolveContext {
  let isAdmin = false
  let isImpersonated = false
  try {
    const profile = useUserStore().profile
    isAdmin = !!profile?.IsAppAdmin
    isImpersonated = !!profile?.Impersonated
  } catch {
    isAdmin = false
    isImpersonated = false
  }

  let flags: Record<string, unknown> = {}
  try {
    flags = useDatapoints() as unknown as Record<string, unknown>
  } catch {
    flags = {}
  }

  function isFlagEnabled(flag: string): boolean {
    const entry = flags[flag] as { value?: boolean } | undefined
    if (!entry || typeof entry.value !== 'boolean') return true // fail open
    return entry.value
  }

  return { isAdmin, isImpersonated, isFlagEnabled }
}
